import { computed, MaybeRefOrGetter, toValue } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { sumCheckCounts } from '@/components/Checklists/checkCounts'
import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'
import { graphql } from '@/gql'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

// Only the company that owns the hire can read its checklists, so nobody else gets the query.
export function useHireChecklists(hireId: MaybeRefOrGetter<string>) {
  const { isCompany } = useAccountCapabilities()
  const { viewerIsSupplier, loading: roleLoading, error: roleError } = useHireRecruiter(hireId)

  // A supplier looks like a client until the role has loaded, so wait for it.
  const viewingAsClient = computed(
    () => isCompany.value && !roleLoading.value && !roleError.value && !viewerIsSupplier.value,
  )

  const { result, loading: queryLoading, error } = useQuery(
    graphql(`
      query HireChecklistCounts($id: ID!) {
        hire(id: $id) {
          id
          checklists {
            id
            checkCounts {
              total
              done
              blockingPending
            }
          }
        }
      }
    `),
    () => ({ id: toValue(hireId) }),
    { clientId: 'publicApi', enabled: viewingAsClient },
  )

  // After a hire change, result still holds the previous hire's data until the new request settles.
  const checklists = computed(() =>
    queryLoading.value || error.value ? [] : (result.value?.hire?.checklists ?? []),
  )

  // A newly enabled query only starts on the next tick, so it is pending until it has data or an error.
  const awaitingQuery = computed(
    () => viewingAsClient.value && result.value === undefined && !error.value,
  )

  return {
    // Tabs are judged on this, so it has to cover the waits before the checklists are known.
    loading: computed(() => roleLoading.value || queryLoading.value || awaitingQuery.value),
    checklists,
    hasChecklists: computed(() => viewingAsClient.value && checklists.value.length > 0),
    checkCounts: computed(() => sumCheckCounts(checklists.value)),
  }
}
